/**
 * CU-BEMS — real measured ground truth for Chamchuri 5.
 *
 * Unlike `energyModel.ts` (which MODELS ~24k buildings from public OSM
 * footprints), this module carries ACTUAL metered data: Chulalongkorn's
 * Building Energy Management System recorded Chamchuri 5 at 1-minute
 * resolution across all 7 floors for 2019 (3.58M samples). We derived a
 * compact hourly profile + headline stats from the published dataset and
 * vendor it as `cubems-cham5-2019.json`.
 *
 * Dataset: Pipattanasomporn et al., "CU-BEMS, smart building electricity
 * consumption and indoor environmental sensor datasets", Scientific Data
 * 7:241 (2020). CSVs: https://sgrudata.github.io/
 *
 * This gives the dashboard one building where the energy number is not a
 * guess — and an anchor to calibrate the synthetic model against.
 */

import raw from "../data/cubems-cham5-2019.json";

export interface CubemsProfile {
  source: string;
  building: string;
  citation: string;
  dataUrl: string;
  year: number;
  floorAreaM2: number;
  floors: number;
  annualKwh: number;
  euiKwhM2Yr: number;
  peakHourlyAvgKw: number;
  endUseSharePct: { ac: number; lighting: number; plug: number };
  comfort: { meanTempC: number; meanRhPct: number };
  hourlyKwWeekday: number[];
  hourlyKwWeekend: number[];
  minuteSamples: number;
}

export const CHAM5: CubemsProfile = raw as CubemsProfile;

/** Bangkok-local hour (0–23) for a given instant. */
function bangkokHour(at: Date): number {
  return Number(
    new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Bangkok", hour: "2-digit", hour12: false }).format(at),
  );
}

/** Bangkok-local day-of-week (0=Sun … 6=Sat) for a given instant. */
function bangkokDow(at: Date): number {
  return new Date(at.toLocaleString("en-US", { timeZone: "Asia/Bangkok" })).getDay();
}

/**
 * Chamchuri 5's REAL measured draw (kW) for the current Bangkok hour,
 * read straight from the 2019 metered hourly profile. Weekend hours use
 * the weekend curve (the building is mostly idle Sat–Sun). This is the
 * actual building behaviour, not a model output.
 */
export function cham5MeasuredKw(at: Date = new Date()): number {
  const hour = bangkokHour(at);
  const dow = bangkokDow(at);
  const isWeekend = dow === 0 || dow === 6;
  const curve = isWeekend ? CHAM5.hourlyKwWeekend : CHAM5.hourlyKwWeekday;
  return curve[hour] ?? curve[0];
}

/** Day-type label for the current measured reading. */
export function cham5DayType(at: Date = new Date()): "weekday" | "weekend" {
  const dow = bangkokDow(at);
  return dow === 0 || dow === 6 ? "weekend" : "weekday";
}
